// random polyfills
import 'react-app-polyfill/ie9';
import 'react-app-polyfill/stable';
import 'intersection-observer';

import ReactDOM from 'react-dom';
import Provider from 'react-redux/es/components/Provider';
import { BrowserRouter } from 'react-router-dom';
import MomentAdapter from '@material-ui/pickers/adapter/moment'; // choose your lib
import { LocalizationProvider } from '@material-ui/pickers';
import { SnackbarProvider } from 'notistack';
import * as Sentry from '@sentry/react';

import { MuiThemeProvider } from '@material-ui/core/styles';
import moment from './moment-hawaii';
import config from './config';

import './style.css';
import store from './reducers/store';
import App from './components/App';

import * as serviceWorker from './serviceWorker';
import { ModalContextProvider, Modals } from './hooks/useModal';
import { setIslands } from './hooks/useIslands';
import CookieBar from './components/CookieBar';
import { doSwaggerCall } from './hooks/useSwaggerClient';
import { reportGlobalError } from './helpers/chaosReporter';
import customTheme from './theme';
import { setGuestFields } from './hooks/useGuestFields';
import { setPaymentMethods } from './hooks/usePaymentMethod';
import GoogleAnalyticsProvider from './hooks/useGoogleAnalytics';
import {
    releaseGuestLock,
    setInitialLockHolders,
    startLockRefresh,
    tryAcquireGuestLock
} from './tools/softlock';
import { loginUser, refreshUserCapabilities, updateFreshCustomerData } from './actions/user';
import { readSharedSession, writeSharedSession } from './helpers/crossUiSession';

window.addEventListener('error', e => reportGlobalError('window.error', e.error || e));
window.addEventListener('unhandledrejection', e =>
    reportGlobalError('unhandledrejection', e.reason)
);

(function() {
    function additem(url, iscss, options) {
        const head = document.head || document.getElementsByTagName('head')[0];
        const s = document.createElement(iscss ? 'link' : 'script');
        if (iscss) {
            s.type = 'text/css';
            s.href = url;
            s.rel = 'stylesheet';
            s.crossorigin = 'anonymous';
            Object.entries(options).forEach((key, value) => {
                s[key] = value;
            });
        } else {
            s.src = url;
        }
        head.appendChild(s);
    }
    additem(`${config.assetsUrl}/assets/fontawesome/css/all.min.css`, true, {});
})();

/**
 * The react init is delayed, since we need to grab client based data from the server first
 */
export default function startReactCode(clientData) {
    // do this only on staging + production
    if (
        window.location.href.indexOf('localhost') === -1 &&
        window.location.href.indexOf('127.0.0.1') === -1
    ) {
        Sentry.init({
            environment: config.sentryEnv,
            dsn: 'https://e56be71b829d484ba6f82e79ff6d1529@o1005407.ingest.sentry.io/5966101',
            // Or however deep you want your state context to be.
            normalizeDepth: 12,
            // https://github.com/getsentry/sentry-javascript/issues/3440
            ignoreErrors: [
                'Non-Error promise rejection captured',
                'ResizeObserver loop completed with undelivered notifications.'
            ],
            beforeSend(event) {
                // was: beforeSend(event, hint)
                // Check if it is an exception, and if so, show the report dialog
                if (event.exception) {
                    Sentry.showReportDialog({ eventId: event.event_id });
                }
                return event;
            }
        });
    }
    const theme = customTheme({
        primaryColor: clientData.primaryColor,
        textColor: clientData.textColor,
        secondaryColor: clientData.secondaryColor,
        fontFamily: clientData.fontFamily
    });
    // Normalize defaultIsland - handle old format (string) and new format (array)
    const normalizedDefaultIsland = Array.isArray(clientData.defaultIsland)
        ? clientData.defaultIsland
        : typeof clientData.defaultIsland === 'string' && clientData.defaultIsland.length > 0
        ? [clientData.defaultIsland]
        : [];

    window.clientData = {
        ...clientData,
        tags: clientData.tags || [],
        defaultIsland: normalizedDefaultIsland
    };

    ReactDOM.render(
        <Sentry.ErrorBoundary>
            <Provider store={store}>
                <LocalizationProvider
                    dateAdapter={MomentAdapter}
                    dateLibInstance={moment}
                    locale="en"
                >
                    <BrowserRouter>
                        <MuiThemeProvider theme={theme}>
                            <GoogleAnalyticsProvider client={clientData}>
                                <CookieBar clientData={clientData} />
                                <ModalContextProvider>
                                    <SnackbarProvider>
                                        <App />
                                        <Modals />
                                    </SnackbarProvider>
                                </ModalContextProvider>
                            </GoogleAnalyticsProvider>
                        </MuiThemeProvider>
                    </BrowserRouter>
                </LocalizationProvider>
            </Provider>
        </Sentry.ErrorBoundary>,
        document.getElementById('app')
    );
}

if (typeof window.ACTIVITER_CLIENT === 'undefined') {
    document.getElementById('app').textContent =
        'ACTIVITER_CLIENT is not defined, please consult with the documentation on how to setup an Activiter client.';
} else {
    async function init() {
        try {
            const clientData = await doSwaggerCall('clients', 'getClientInfo');

            // Set state
            setPaymentMethods(clientData?.payment_methods);
            setIslands({
                newIslands: clientData?.islands,
                newVisibleIslands: clientData?.visibleIslands
            });
            setGuestFields(clientData?.guest_fields, clientData?.header_guest_fields);

            // Cross-UI session bridge: if the new frontend established a
            // session (shared blob) but this app has none, adopt it so a
            // UI toggle keeps the user logged in. If this app already has
            // a session, mirror it out so the new frontend can adopt it.
            const persisted = store.getState()?.user;
            if (!persisted?.user) {
                const shared = readSharedSession();
                if (shared) {
                    store.dispatch(loginUser({ ...shared.user, token: shared.token }));
                }
            } else if (persisted.user.token) {
                writeSharedSession(persisted.user.token, persisted.user);
            }

            const { user, customer } = store.getState()?.user;

            // the jwt-oauth hand-off page logs out and re-authenticates from
            // the partner token, so any persisted (and likely expired) session
            // here would only produce a 401 on /users/me
            const isJwtOauthHandoff = window.location.pathname.startsWith('/jwt-oauth-login');

            if (user && !isJwtOauthHandoff) {
                refreshUserCapabilities()(store.dispatch, store.getState);
            }

            if (customer && !isJwtOauthHandoff) {
                updateFreshCustomerData()(store.dispatch, store.getState);
            }

            // Guest lock handling
            if (
                customer?.id &&
                ['agent', 'admin'].includes(user?.role) &&
                customer?.id !== user?.id
            ) {
                try {
                    const result = await tryAcquireGuestLock(customer.id);
                    setInitialLockHolders(result.lockHolders);
                } catch (err) {
                    // we are failing silently here to not block login
                    console.info('Failed to acquire guest lock initialy:', err);
                }
                startLockRefresh();
            }

            // Always start React after lock logic completes
            return startReactCode(clientData);
        } catch (err) {
            console.error('Failed to fetch client data:', err);
            try {
                await releaseGuestLock();
            } catch (lockErr) {
                console.error('Failed to release lock:', lockErr);
            }
            document.getElementById('app').textContent =
                'ACTIVITER_CLIENT is not defined, please consult with the documentation on how to setup an Activiter client.';
        }
    }

    init().catch(err => {
        console.error('Initialization error:', err);
        document.getElementById('app').textContent =
            'An error occurred during initialization. Please try again later.';
    });
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
